import React, { useState, useEffect } from 'react';
import styled from 'styled-components/macro';
import { useSelector } from 'react-redux';
import { ResponseT } from '@utils/api/base';
import { ListError, ListLoader } from '@components/elements/listStyles';
import { RootState } from '../../store';
import useOrders from './useOrders';
import { OrderL } from '../../type';
import useCognito from '@as_core/account/useCognito';
import { FlexRow } from '@components/layout/FlexStyles';
import BasicTable from '@components/composite/BasicTable';
import { getAPI } from '@components/submissions/utils';
import IconDownload from '@components/icons/IconDownload';

const tableFields = [
  { value: 'number', label: 'Order Number', width: 200 },
  { value: 'orderDate', label: 'Order Date', width: 180 },
  { value: 'credits', label: 'Credits', width: 50 },
  { value: 'cost', label: 'Total Cost', width: 120 },
  { value: 'status', label: 'Status', width: 80 },
  { value: 'invoice', label: 'Invoice', width: 150 },
];

const getTableRows = (orders) => {
  let rows = [];
  for (const order of orders) {
    let row = {};
    for (const field of tableFields) {
      if (!['orderDate', 'cost', 'invoice'].includes(field.value)) {
        row[field.value] = order[field.value];
      } else if (field.value === 'orderDate') {
        row[field.value] =
          new Date(order.orderDate).toLocaleDateString() +
          ' ' +
          new Date(order.orderDate).toLocaleTimeString();
      } else if (field.value === 'cost') {
        row[field.value] = order.value.toFixed(2) + ' ' + order.currency;
      } else if (field.value === 'invoice') {
        row[field.value] = (
          <StyledA
            href={getAPI() + `/api/invoice/download/${order.invoiceId}`}
            className='text-deep my-0'
            download
            onClick={(e) => e.stopPropagation()}
          >
            <IconDownload />
          </StyledA>
        );
      }
    }
    rows.push(row);
  }
  return rows;
};

const debug = false;
const OrdersList = () => {
  const { getOrders } = useOrders();
  const orders = useSelector((state: RootState) => state.orders);
  const { getToken } = useCognito();

  // ###########################################################################
  // INFINITE LOADING
  // ###########################################################################
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadedAll, setLoadedAll] = useState(false);

  debug && console.log('ordersViews {views}', orders);

  const loadData = React.useCallback(async () => {
    setLoading(true);
    let res: ResponseT<OrderL>;

    const params: { [propName: string]: any } = {
      take: 10,
      skip: 0,
    };

    res = await getOrders(getToken(), params);

    debug && console.log(res);

    setData(res);
    setLoading(false);
    // setLoadedAll(!res.continues);
  }, []);

  const loadMore = async () => {
    setLoadingMore(true);
    let res: ResponseT<OrderL>;

    const params: { [propName: string]: any } = {
      start: 1,
      limit: 50,
    };

    res = await getOrders(getToken(), params);
    setData([...data]);
    setLoading(false);
  };

  const handleEndReached = () => {
    if (loading || loadingMore || loadedAll) return;
    loadMore().then();
  };

  useEffect(() => {
    if (orders && loading === false) {
      loadData().then();
    }
  }, [orders, loadData]);

  const [sortBy, setSortBy] = React.useState({});
  const filteredOrders = data?.orderList.filter((o) => o?.status === 'paid');

  return (
    <>
      {data != null && !loading ? (
        <ListContainer>
          <BasicTable
            fields={tableFields}
            rows={getTableRows(filteredOrders)}
            border={false}
          />
        </ListContainer>
      ) : (
        <>
          {' '}
          {loading ? (
            <FlexRow width={'100%'} height={'200px'} v_centered h_centered>
              <ListLoader />
            </FlexRow>
          ) : (
            <ListError>
              Missing Orders or the filter settings did not return any orders
            </ListError>
          )}
        </>
      )}
    </>
  );
};

export default OrdersList;

const ListContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

const StyledA = styled.a`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  filter: grayscale(1) brightness(${(p) => p.theme.menu.inActive.brightness})
    contrast(${(p) => p.theme.menu.inActive.contrast});
  :hover {
    filter: grayscale(0) brightness(${(p) => p.theme.menu.active.brightness})
      contrast(${(p) => p.theme.menu.active.contrast});
  }
`;
